import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Heart, Headphones, Linkedin, Twitter, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Navbar from '../../components/landing/Navbar';
import Footer from '../../components/landing/Footer';
import LoginModal from '../../components/landing/LoginModal';
import ContactModal from '../../components/landing/ContactModal';
import FAQModal from '../../components/landing/FAQModal';
import ClientPortalModal from '../../components/landing/ClientPortalModal';
import { Toaster } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL (handles both relative and absolute paths)
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API}${path}`;
};

// Helper to ensure external URLs have https:// prefix
const ensureAbsoluteUrl = (url) => {
  if (!url) return '';
  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed}`;
};

// Testimonial Carousel Component
const TestimonialCarousel = ({ testimonials }) => {
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);
  const count = testimonials.length;

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (count > 1) {
      timerRef.current = setInterval(() => {
        setActive(prev => (prev + 1) % count);
      }, 6000);
    }
  }, [count]);

  useEffect(() => {
    resetTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [resetTimer]);

  const goTo = (idx) => { setActive(idx); resetTimer(); };
  const prev = () => goTo((active - 1 + count) % count);
  const next = () => goTo((active + 1) % count);

  const t = testimonials[active];
  if (!t) return null;

  return (
    <section className="py-20 px-4 bg-slate-900/50" data-testid="testimonial-carousel">
      <div className="max-w-4xl mx-auto text-center relative">
        {/* Quote icon */}
        <svg className="mx-auto mb-6 w-12 h-12 text-amber-500/20" fill="currentColor" viewBox="0 0 32 32">
          <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H8c0-1.1.9-2 2-2V8zm14 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-1.1.9-2 2-2V8z"/>
        </svg>

        {/* Slide content with fade transition */}
        <div className="min-h-[180px] flex flex-col items-center justify-center">
          <blockquote
            key={active}
            className="text-xl sm:text-2xl lg:text-3xl text-white font-light leading-relaxed mb-8 animate-fadeIn"
          >
            "{t.quote}"
          </blockquote>
          <div>
            <p className="text-amber-400 font-medium">{t.author}</p>
            {t.role && <p className="text-slate-500 text-sm">{t.role}</p>}
          </div>
        </div>

        {/* Navigation arrows (only if > 1) */}
        {count > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-0 top-1/2 -translate-y-1/2 p-2 rounded-full text-slate-500 hover:text-amber-400 hover:bg-slate-800/60 transition-all"
              aria-label="Previous testimonial"
              data-testid="testimonial-prev"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={next}
              className="absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-full text-slate-500 hover:text-amber-400 hover:bg-slate-800/60 transition-all"
              aria-label="Next testimonial"
              data-testid="testimonial-next"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Dot indicators (only if > 1) */}
        {count > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8" data-testid="testimonial-dots">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goTo(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  idx === active
                    ? 'bg-amber-400 w-6'
                    : 'bg-slate-600 hover:bg-slate-500'
                }`}
                aria-label={`Go to testimonial ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Fade-in animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </section>
  );
};

const AboutUs = () => {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [loginInitialStep, setLoginInitialStep] = useState('email');
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isFAQOpen, setIsFAQOpen] = useState(false);
  const [isClientPortalOpen, setIsClientPortalOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/api/about-us`);
        setContent(response.data);
      } catch (error) {
        console.error('Failed to fetch about us content:', error);
        // Default content
        setContent({
          hero_headline: "We're on a mission to ensure every family is protected.",
          hero_subtext: "Making estate planning simple, accessible, and affordable for everyone.",
          hero_image: null,
          values: [
            { id: '1', icon: 'simplicity', title: 'Simplicity', description: 'Create your will in minutes, not hours' },
            { id: '2', icon: 'security', title: 'Security', description: 'Bank-grade encryption for your data' },
            { id: '3', icon: 'support', title: 'Support', description: 'Expert help whenever you need it' }
          ],
          testimonial: {
            quote: "WillKart gave us peace of mind knowing our children's future is secure.",
            author: "Satisfied Customer",
            role: "Parent of 2"
          },
          testimonials: [
            { quote: "WillKart gave us peace of mind knowing our children's future is secure.", author: "Satisfied Customer", role: "Parent of 2" }
          ],
          team: [
            { id: '1', name: 'Jay Shah', role: 'Founder & CEO', photo: null, linkedin: '', twitter: '' }
          ],
          team_heading: "Meet Our Team",
          team_subtext: "Passionate experts dedicated to protecting your family's future",
          cta_text: "Start Your Will Today",
          cta_link: "/",
          show_testimonial: true,
          show_team: true,
          show_values: true
        });
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const getValueIcon = (iconName) => {
    switch (iconName) {
      case 'simplicity': return <Heart className="w-8 h-8" />;
      case 'security': return <Shield className="w-8 h-8" />;
      case 'support': return <Headphones className="w-8 h-8" />;
      default: return <Heart className="w-8 h-8" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar 
        onLoginClick={() => { setLoginInitialStep('email'); setIsLoginOpen(true); }} 
        onContactClick={() => setIsContactOpen(true)}
        onFAQClick={() => setIsFAQOpen(true)}
        onClientPortalClick={() => setIsClientPortalOpen(true)}
      />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 px-4 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-slate-700/10 rounded-full blur-3xl" />
          </div>
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {content.hero_headline}
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              {content.hero_subtext}
            </p>
          </div>
        </section>

        {/* Hero Image/Video Section */}
        {content.hero_image && (
          <section className="px-4 pb-16">
            <div className="max-w-5xl mx-auto">
              <div className="rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/50 border border-slate-800">
                <img 
                  src={getImageUrl(content.hero_image)} 
                  alt="About WillKart" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </section>
        )}

        {/* Values Section */}
        {content.show_values && content.values && content.values.length > 0 && (
          <section className="py-20 px-4 border-t border-slate-800/50">
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-3 gap-12">
                {content.values.map((value) => (
                  <div key={value.id} className="text-center group">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/10 text-amber-400 mb-6 group-hover:bg-amber-500/20 transition-colors">
                      {getValueIcon(value.icon)}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                    <p className="text-slate-400">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Testimonials Carousel */}
        {content.show_testimonial && content.testimonials && content.testimonials.length > 0 && (
          <TestimonialCarousel testimonials={content.testimonials} />
        )}
        {/* Backward compat: single testimonial fallback */}
        {content.show_testimonial && (!content.testimonials || content.testimonials.length === 0) && content.testimonial && content.testimonial.quote && (
          <TestimonialCarousel testimonials={[content.testimonial]} />
        )}

        {/* Team Section */}
        {content.show_team && content.team && content.team.length > 0 && (
          <section className="py-20 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {content.team_heading || "Meet Our Team"}
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  {content.team_subtext || "Passionate experts dedicated to protecting your family's future"}
                </p>
              </div>
              
              <div className={`grid gap-8 ${content.team.length === 1 ? 'max-w-sm mx-auto' : content.team.length === 2 ? 'md:grid-cols-2 max-w-2xl mx-auto' : content.team.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
                {content.team.map((member) => (
                  <div key={member.id} className="group text-center">
                    <div className="relative mb-6 mx-auto w-48 h-48 rounded-2xl overflow-hidden bg-slate-800 border border-slate-700 group-hover:border-amber-500/50 transition-colors">
                      {member.photo ? (
                        <img 
                          src={getImageUrl(member.photo)} 
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800">
                          <span className="text-5xl font-bold text-slate-500">
                            {member.name.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-1">{member.name}</h3>
                    <p className="text-amber-400 text-sm mb-4">{member.role}</p>
                    
                    {/* Social Links */}
                    <div className="flex items-center justify-center gap-3">
                      {member.linkedin && (
                        <a 
                          href={ensureAbsoluteUrl(member.linkedin)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-slate-500 hover:text-amber-400 transition-colors"
                        >
                          <Linkedin size={18} />
                        </a>
                      )}
                      {member.twitter && (
                        <a 
                          href={ensureAbsoluteUrl(member.twitter)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-slate-500 hover:text-amber-400 transition-colors"
                        >
                          <Twitter size={18} />
                        </a>
                      )}
                      {member.email && (
                        <a 
                          href={`mailto:${member.email}`}
                          className="text-slate-500 hover:text-amber-400 transition-colors"
                        >
                          <Mail size={18} />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-20 px-4 border-t border-slate-800/50">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8">
              Ready to protect your family's future?
            </h2>
            <Button
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-10 py-6 text-lg rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-amber-400/30 transition-all duration-300 group"
              onClick={() => { setLoginInitialStep('audience'); setIsLoginOpen(true); }}
              data-testid="about-cta-btn"
            >
              {content.cta_text || "Start Your Will Today"}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </section>
      </main>

      <Footer onContactClick={() => setIsContactOpen(true)} />
      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} initialStep={loginInitialStep} />
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
      <FAQModal isOpen={isFAQOpen} onClose={() => setIsFAQOpen(false)} />
      <ClientPortalModal isOpen={isClientPortalOpen} onClose={() => setIsClientPortalOpen(false)} />
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default AboutUs;
